import Api, { buildAndValidateUrl } from '@silbo/core';
import { PropsWithChildren, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { datadogInit } from '../../datadog-init';
import { initLiveQueryClient } from '../../live-query-client';
import { ApiContext } from './ApiContext';
import { ApiConfig } from './types';

export const defaultApiConfig: ApiConfig = {
  id: import.meta.env.VITE_PARSE_APP_ID,
  url: buildAndValidateUrl(
    import.meta.env.VITE_PARSE_URL,
    import.meta.env.VITE_PARSE_URL_PATH === undefined ? '/parse' : import.meta.env.VITE_PARSE_URL_PATH,
    window.location.origin
  ),
  liveQueryUrl: import.meta.env.VITE_PARSE_LIVEQUERY_URL
    ? buildAndValidateUrl(
        import.meta.env.VITE_PARSE_LIVEQUERY_URL,
        import.meta.env.VITE_PARSE_LIVEQUERY_URL_PATH === undefined
          ? '/ws'
          : import.meta.env.VITE_PARSE_LIVEQUERY_URL_PATH,
        window.location.origin
      )
    : window.location.origin,
};

export const ApiProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [apiConfig, setApiConfig] = useState<ApiConfig>(
    localStorage.getItem('apiConfig') ? JSON.parse(localStorage.getItem('apiConfig') as string) : defaultApiConfig
  );

  const value = {
    apiConfig,
    storeApiConfig: (config: ApiConfig) => {
      localStorage.setItem('apiConfig', JSON.stringify(config));
      flushSync(() => setApiConfig(config));
    },
    removeApiConfig: () => {
      localStorage.removeItem('apiConfig');
      setApiConfig(defaultApiConfig);
    },
  };

  // Api initialization needs to be placed here to have access to the apiConfig
  // as soon as it is stored
  useEffect(() => {
    const init = async () => {
      Api.serverURL = apiConfig.url;
      Api.applicationId = apiConfig.id;

      if (apiConfig.liveQueryUrl) {
        await initLiveQueryClient(apiConfig.liveQueryUrl, apiConfig.id);
      }

      datadogInit(Api.serverURL);
    };

    init();
  }, [apiConfig]);

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};
